import React from 'react';
import { Target, Users, Heart, Award, TrendingUp, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const WhoWeAre = () => {
  const boardMembers = [
    {
      name: 'Dylan Firth',
      role: 'Chair',
      organization: 'Brewers Association of New Zealand',
    },
    {
      name: 'Robert Brewer',
      role: 'Board Member',
      organization: 'Spirits NZ',
    },
    {
      name: 'Sarah Wilson',
      role: 'Board Member',
      organization: 'New Zealand Winegrowers',
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxjb21tdW5pdHklMjBzdXBwb3J0fGVufDB8fHx8MTc3MDc2MTU3M3ww&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-teal-900/85 to-cyan-700/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <Users className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Who We Are</h1>
          <p className="text-xl">Creating positive change in New Zealand's drinking culture</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-16">
        {/* Mission Statement */}
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
          <p className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto">
            Cheers is a social change initiative dedicated to creating a healthier, safer, and more responsible drinking culture in New Zealand. Our mission is to strengthen safe and sociable drinking behaviours through education, awareness, and community engagement.
          </p>
        </div>

        {/* Core Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-6">
                <div className="bg-teal-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-10 w-10 text-teal-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Health First</h3>
                <p className="text-gray-600">We prioritize the health and wellbeing of all New Zealanders</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-6">
                <div className="bg-cyan-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Target className="h-10 w-10 text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Evidence-Based</h3>
                <p className="text-gray-600">Our approach is grounded in research and proven strategies</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-6">
                <div className="bg-blue-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Users className="h-10 w-10 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Community Focus</h3>
                <p className="text-gray-600">We work with communities to create lasting change</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
              <CardContent className="p-6">
                <div className="bg-indigo-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <Award className="h-10 w-10 text-indigo-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Excellence</h3>
                <p className="text-gray-600">We strive for excellence in everything we do</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* What We Do */}
        <Card className="mb-12 bg-gradient-to-br from-teal-50 to-cyan-50">
          <CardHeader>
            <CardTitle className="text-2xl">What We Do</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <TrendingUp className="mr-3 h-6 w-6 text-teal-600" />
                Education and Awareness
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We provide evidence-based information and resources to help New Zealanders make informed decisions about alcohol. Our educational programmes reach diverse audiences, from teenagers to parents to community leaders.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <Users className="mr-3 h-6 w-6 text-cyan-600" />
                Community Engagement
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We work directly with communities, schools, and organizations to promote safer drinking practices. Through partnerships and collaborative initiatives, we're creating environments that support healthy choices.
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm">
              <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
                <Heart className="mr-3 h-6 w-6 text-blue-600" />
                Support and Resources
              </h3>
              <p className="text-gray-700 leading-relaxed">
                We connect people with the help they need, whether that's information about standard drinks, tips for safer drinking, or referrals to professional support services for those experiencing alcohol-related harm.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Our Board */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl">Our Board</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {boardMembers.map((member, index) => (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-teal-50 to-cyan-50 p-6 rounded-lg border border-teal-200 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-teal-100 rounded-full p-4">
                      <User className="h-12 w-12 text-teal-600" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                    {member.name}
                  </h3>
                  <p className="text-teal-700 font-semibold text-center mb-2">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-center text-sm">
                    {member.organization}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Industry Partner Links */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border border-blue-200">
              <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">Our Industry Partners</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <a 
                  href="https://spiritsnz.org.nz/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white p-4 rounded-lg text-center hover:shadow-md transition-all duration-200 group"
                >
                  <p className="font-bold text-gray-900 group-hover:text-teal-600 transition-colors">Spirits NZ</p>
                  <p className="text-sm text-gray-600 mt-1">Visit Website →</p>
                </a>
                <a 
                  href="https://www.nzwine.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white p-4 rounded-lg text-center hover:shadow-md transition-all duration-200 group"
                >
                  <p className="font-bold text-gray-900 group-hover:text-teal-600 transition-colors">New Zealand Winegrowers</p>
                  <p className="text-sm text-gray-600 mt-1">Visit Website →</p>
                </a>
                <a 
                  href="https://brewersguild.org.nz/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white p-4 rounded-lg text-center hover:shadow-md transition-all duration-200 group"
                >
                  <p className="font-bold text-gray-900 group-hover:text-teal-600 transition-colors">Brewers Association of NZ</p>
                  <p className="text-sm text-gray-600 mt-1">Visit Website →</p>
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* The Tomorrow Project */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="text-2xl">Part of The Tomorrow Project</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed mb-4">
              Cheers is an initiative of The Tomorrow Project, an organization committed to creating positive social change in New Zealand. The Tomorrow Project brings together research, innovation, and community action to address some of our country's most pressing social issues.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Through The Tomorrow Project's support, Cheers has access to expertise, resources, and networks that amplify our impact and help us reach more New Zealanders with life-saving information.
            </p>
          </CardContent>
        </Card>

        {/* Our Impact */}
        <Card className="mb-12 bg-gradient-to-br from-amber-50 to-orange-50">
          <CardHeader>
            <CardTitle className="text-2xl">Our Impact</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-6">
              Through our work, we're making a real difference in how New Zealanders think about and consume alcohol:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="text-gray-700">✓ Increased awareness of standard drinks and safer drinking guidelines</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="text-gray-700">✓ More conversations between parents and teens about alcohol</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="text-gray-700">✓ Greater understanding of alcohol's effects during pregnancy</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="text-gray-700">✓ Improved knowledge among young people about alcohol risks</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="text-gray-700">✓ Stronger community support for safer drinking practices</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <p className="text-gray-700">✓ Enhanced access to help for those experiencing alcohol harm</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Looking Forward */}
        <Card className="bg-gradient-to-br from-teal-600 to-cyan-700 text-white">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold mb-4 text-center">Looking Forward</h3>
            <p className="text-lg leading-relaxed text-center max-w-3xl mx-auto">
              We believe in a future where all New Zealanders can enjoy alcohol safely and responsibly, where young people delay drinking until they're ready, where pregnant women are supported to avoid alcohol, and where communities work together to prevent alcohol-related harm. We're committed to making this vision a reality.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WhoWeAre;